/**
 * VIEW AIRPLANES, as a state machine — the shell's aircraft-recognition
 * screen, not `/assets`'s developer viewer (AD-009: "`/assets` stays a modern
 * tool… it's a dev/review surface, not the game").
 *
 * The original's own flow, recovered from `PS.EXE`'s string table this pass
 * (`strings -n 4`, `data/original/RedBaron/SIERRA/REDBARON/PS.EXE`) and one
 * live capture (`docs/game/ui-reference/view-airplanes-3d.png`,
 * `index.md`'s "View Airplanes → 3D viewer" section): the Main Menu's
 * `VIEW AIRPLANES` opens a side-picker dialog — the string table gives its
 * exact title, `VIEW WHOSE AIRPLANES?`, immediately followed in the binary by
 * `Famous Aces` (a third option `ui-reference/index.md` flagged as
 * undocumented, and this module's answer to that flag — see below) — then a
 * live-rendered turntable with a name header, one paragraph of flavor text,
 * and `PREVIOUS` / `NEXT` / `CONTINUE` (all three strings recovered verbatim,
 * same table).
 *
 * **Roster**: `German Air Service` / `Royal Flying Corps` each browse
 * `@red-opus/engine`'s `SCOUT_KEYS` filtered by `nationOf` — the 22
 * player-flyable scouts (`docs/game/aircraft.md`'s FIGHTER.DAT roster; see
 * `aircraft-flavor.ts`'s header for why that document's own "20" undercounts
 * it by two). Not the two-seaters/bombers: the picker only ever offers a side,
 * never a "everything" option, and `RBMB.EXE`'s own rule — "the player may
 * only fly a scout plane" — is the same restriction this screen mirrors.
 *
 * **Famous Aces, port-side.** `PS.EXE` carries `SELECT THE ACE YOU ARE
 * INTERESTED IN:` and `'s planes` right beside `PREVIOUS`/`CONTINUE` in the
 * string table, confirming a real sub-flow existed, but no `.DAT` decode this
 * project has done recovers a full per-ace aircraft list to drive a roster
 * screen from (`docs/game/aces-squadrons.md` §6: `APLANE.DAT` gave up only
 * Richthofen's and Udet's mount *progressions* before the extraction pass
 * that read it ran out of file). What the engine already has —
 * `mission-roster.ts`'s `FAMOUS_ACES`, 25 named aces each tagged with one
 * historically-grounded mount — is reused rather than re-invented: choosing
 * `Famous Aces` pages through those 25 directly (German first, then Allied,
 * `ACE.DAT`'s own order) instead of opening a second selection screen, which
 * sidesteps needing a scrollable list control this shell's widget set does
 * not have a keyboard-navigable version of yet (`shell-screen.ts`'s `list`
 * kind is one focus stop, not one per row). The title becomes `<ace>'s
 * planes`, echoing the recovered string, and the blurb is the ace's own bio
 * line ahead of the aeroplane's flavor text.
 */

import { aircraftLabel, FAMOUS_ACES, nationOf, SCOUT_KEYS, type AircraftKey, type FamousAce, type Nation } from "@red-opus/engine";
import { clampZoom, INITIAL_POSE, YAW_STEP_DEG, type PreviewPose } from "../../career/preview-pose.ts";
import { AIRCRAFT_FLAVOR } from "../../planes/aircraft-flavor.ts";
import type { ShellEffect } from "./menu-model.ts";
import { dialogRow, type ShellDialog } from "./shell-screen.ts";

export type PlaneRosterMode = { readonly kind: "nation"; readonly nation: Nation } | { readonly kind: "aces" };

export interface PlaneViewerModel {
  readonly mode: PlaneRosterMode;
  readonly index: number;
  readonly dialog: ShellDialog | undefined;
  readonly focus: number;
  /** The same pose shape PERSONAL AIRCRAFT's turntable uses (`career/preview-pose.ts`) — reused, not re-derived. */
  readonly pose: PreviewPose;
}

/** `VIEW WHOSE AIRPLANES?` — `PS.EXE`'s own title, immediately followed in its string table by these three options. */
export function viewWhoseAirplanesDialog(): ShellDialog {
  return {
    title: "VIEW WHOSE AIRPLANES?",
    body: [],
    rows: [
      dialogRow("german", "German Air Service", { kind: "command", command: "nation:german" }, "cross"),
      dialogRow("allied", "Royal Flying Corps", { kind: "command", command: "nation:allied" }, "roundel"),
      dialogRow("aces", "Famous Aces", { kind: "command", command: "aces" }),
      dialogRow("cancel", "CANCEL", { kind: "close" }),
    ],
    focus: 0,
  };
}

/**
 * Where this screen's camera starts — the shared ladder's *nearest* step,
 * rather than PERSONAL AIRCRAFT's second one.
 *
 * That screen's turntable floats in a well, where one notch out is what keeps
 * both zoom arrows useful. This one stands the aeroplane on a measured
 * aerodrome, and the original's own capture puts its lens about 12 m from the
 * wheels (`planes/parked-look.ts` has the derivation, taken off the shadow's
 * reach below the skyline). At the Eindecker's radius the ladder's two nearest
 * rungs are 13.6 m and 18 m, so the nearest one is the rung that frames the
 * aeroplane the way the original frames it.
 */
export const VIEWER_INITIAL_POSE: PreviewPose = { ...INITIAL_POSE, zoom: 0 };

export function initialPlaneViewer(): PlaneViewerModel {
  return {
    mode: { kind: "nation", nation: "german" },
    index: 0,
    dialog: viewWhoseAirplanesDialog(),
    focus: 0,
    pose: VIEWER_INITIAL_POSE,
  };
}

/** The aeroplanes the current mode pages through, in the order `PREVIOUS`/`NEXT` walk. */
export function rosterFor(mode: PlaneRosterMode): readonly AircraftKey[] {
  if (mode.kind === "aces") return FAMOUS_ACES.map((ace) => ace.aircraft);
  return SCOUT_KEYS.filter((key) => nationOf(key) === mode.nation);
}

function clampIndex(mode: PlaneRosterMode, index: number): number {
  const count = rosterFor(mode).length;
  if (count === 0) return 0;
  return ((index % count) + count) % count;
}

export function currentAircraft(model: PlaneViewerModel): AircraftKey | undefined {
  const roster = rosterFor(model.mode);
  return roster[clampIndex(model.mode, model.index)];
}

/** The Famous Ace currently on screen, or `undefined` outside Famous Aces mode — what the turntable's livery pick (`ace-livery.ts`) keys on. */
export function currentAce(model: PlaneViewerModel): FamousAce | undefined {
  if (model.mode.kind !== "aces") return undefined;
  return FAMOUS_ACES[clampIndex(model.mode, model.index)];
}

/** The name header — the aircraft's, or (Famous Aces) the ace's, matching `PS.EXE`'s `'s planes` string. */
export function viewerTitle(model: PlaneViewerModel): string {
  if (model.mode.kind === "aces") {
    const ace = currentAce(model);
    return ace ? `${ace.name}'s planes` : "FAMOUS ACES";
  }
  const key = currentAircraft(model);
  return key ? aircraftLabel(key) : "";
}

/** The prose under the turntable — `FTEXT.DAT`'s own words, prefixed with the ace's bio line in Famous Aces mode. */
export function viewerBlurb(model: PlaneViewerModel): string {
  const key = currentAircraft(model);
  if (!key) return "";
  const flavor = AIRCRAFT_FLAVOR[key] ?? "";
  const ace = currentAce(model);
  if (!ace) return flavor;
  return `${aircraftLabel(key)} — ${ace.note}`;
}

export interface PlaneViewerTransition {
  readonly model: PlaneViewerModel;
  readonly effect: ShellEffect | undefined;
}

function stay(model: PlaneViewerModel): PlaneViewerTransition {
  return { model, effect: undefined };
}

function chooseMode(model: PlaneViewerModel, mode: PlaneRosterMode): PlaneViewerTransition {
  return stay({ ...model, mode, index: 0, dialog: undefined, focus: 0, pose: VIEWER_INITIAL_POSE });
}

/**
 * Applies one named command. Unknown commands are ignored, matching every
 * other surface's model — a screen that grows a control should not be able
 * to crash the surface.
 */
export function planeViewerCommand(model: PlaneViewerModel, command: string): PlaneViewerTransition {
  if (command === "nation:german") return chooseMode(model, { kind: "nation", nation: "german" });
  if (command === "nation:allied") return chooseMode(model, { kind: "nation", nation: "allied" });
  if (command === "aces") return chooseMode(model, { kind: "aces" });
  if (command === "close") return stay({ ...model, dialog: undefined });
  if (command === "prev") return stay({ ...model, index: clampIndex(model.mode, model.index - 1), pose: VIEWER_INITIAL_POSE });
  if (command === "next") return stay({ ...model, index: clampIndex(model.mode, model.index + 1), pose: VIEWER_INITIAL_POSE });
  if (command === "view:rotate-left") {
    return stay({ ...model, pose: { ...model.pose, yawDeg: (model.pose.yawDeg - YAW_STEP_DEG + 360) % 360 } });
  }
  if (command === "view:rotate-right") {
    return stay({ ...model, pose: { ...model.pose, yawDeg: (model.pose.yawDeg + YAW_STEP_DEG) % 360 } });
  }
  if (command === "view:zoom-in") return stay({ ...model, pose: { ...model.pose, zoom: clampZoom(model.pose.zoom - 1) } });
  if (command === "view:zoom-out") return stay({ ...model, pose: { ...model.pose, zoom: clampZoom(model.pose.zoom + 1) } });
  if (command === "return") return { model, effect: { kind: "navigate", path: "/" } };
  return stay(model);
}
